import React, { createContext, useContext, useEffect } from 'react';
import { ReactComponent as ArrowChevronDownIcon } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { Button } from '@rocco/components/button';
import { useBoolean } from 'ahooks';

interface FilterDropdownProps extends React.PropsWithChildren {
    title: string;
    selected?: boolean;
    closeOnScroll?: boolean;
}

interface FilterDropdownContextType {
    isOpen: boolean;
    closeFilterDropdown: () => void;
}

const FilterDropdownContext = createContext<
    FilterDropdownContextType | undefined
>(undefined);

export const useFilterDropdownContext = () => {
    const context = useContext(FilterDropdownContext);

    if (!context) {
        throw new Error(
            'useFilterDropdownContext must be used within a FilterDropdownProvider',
        );
    }

    return context;
};

export const FilterDropdown = (props: FilterDropdownProps) => {
    const { children, title, closeOnScroll = false } = props;
    const [isOpen, { set: setIsOpen, setFalse: closeFilterDropdown }] =
        useBoolean(false);

    useEffect(() => {
        let lastScrollY = window.scrollY;

        const handleScroll = () => {
            if (!closeOnScroll) return;
            const scrollDiff = Math.abs(window.scrollY - lastScrollY);
            if (scrollDiff > 300) {
                closeFilterDropdown();
                lastScrollY = window.scrollY;
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [closeFilterDropdown, closeOnScroll]);

    return (
        <FilterDropdownContext.Provider value={{ isOpen, closeFilterDropdown }}>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>
                    <Button
                        color={props.selected ? 'primary' : 'secondary'}
                        layoutMode="noShrink"
                    >
                        <span>{title}</span>
                        <ArrowChevronDownIcon className="ml-2 size-4.5 transition-transform group-data-[state=open]:[transform:rotateX(180deg)]" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    paddings="small"
                    avoidCollisions={false}
                >
                    {children}
                </PopoverContent>
            </Popover>
        </FilterDropdownContext.Provider>
    );
};
